import { NgStyle } from '@angular/common';
import {
  Component,
  OnInit,
  AfterViewInit,
  ViewContainerRef,
  inject,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AgGridModule,
  ICellRendererAngularComp,
  ICellEditorAngularComp,
} from 'ag-grid-angular';
import { ICellRendererParams, ICellEditorParams } from 'ag-grid-community';
import { MaterialModule } from '../../material.module';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
//import { doTextArea } from '../jkabout/jkabout.component';
import { idNameItem } from '../../services/jkInterface';
import { PageService } from '../../services/page.service';

/**
 * @fileoverview
 * 此檔案包含與 AG Grid 相關的各種自訂單元格渲染器和編輯器組件。
 * 這些組件用於在 AG Grid 中實現特定的單元格顯示和編輯功能。
 */



/**
 * HTML內容渲染組件
 *
 * 用於在AG Grid中安全地渲染HTML內容的單元格渲染器。
 * 此組件使用Angular的安全機制處理HTML內容以避免XSS攻擊。
 */
@Component({
  selector: 'app-ag-html-renderer',
  standalone: true,
  imports: [FormsModule],
  // template: `<app-quill-html [html]="params.value" style="max-height: 48px;"></app-quill-html>`
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `<div [innerHTML]="html" style="line-height: 1.2;"></div>`,
})
export class AgHtmlCellRenderer implements ICellRendererAngularComp {
  public params: any;
  html: SafeHtml = '';
  page: PageService = inject(PageService);
  constructor() { }
  agInit(params: any): void {
    this.params = params;
    this.html = this.page.sanitizer.bypassSecurityTrustHtml(this.params.value); // (orm.page as BasePage)
  }

  refresh(params: any): boolean {
    return false;
  }
}

/**
 * Markdown 內容渲染組件
 *
 * 將 AG Grid 儲存格中的 Markdown 轉為已清理的 HTML 後顯示。
 */
@Component({
  selector: 'app-ag-markdown-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `<div class="ag-markdown-viewer" [innerHTML]="html"></div>`,
  styles: [`
    :host { display: block; white-space: normal; }
    .ag-markdown-viewer { line-height: 1.5; overflow-wrap: anywhere; color: #0f172a; }
    ::ng-deep .ag-markdown-viewer > :first-child { margin-top: 0; }
    ::ng-deep .ag-markdown-viewer > :last-child { margin-bottom: 0; }
    ::ng-deep .ag-markdown-viewer p { margin: .5rem 0; }
    ::ng-deep .ag-markdown-viewer :is(h1, h2, h3, h4, h5, h6) { margin: 1rem 0 .5rem; font-weight: 700; line-height: 1.25; }
    ::ng-deep .ag-markdown-viewer h1 { font-size: 2rem; }
    ::ng-deep .ag-markdown-viewer h2 { font-size: 1.5rem; }
    ::ng-deep .ag-markdown-viewer h3 { font-size: 1.25rem; }
    ::ng-deep .ag-markdown-viewer h4 { font-size: 1.125rem; }
    ::ng-deep .ag-markdown-viewer h5 { font-size: 1rem; }
    ::ng-deep .ag-markdown-viewer h6 { font-size: .875rem; }
    ::ng-deep .ag-markdown-viewer a { color: #2563eb; font-weight: 500; text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: .15em; }
    ::ng-deep .ag-markdown-viewer a:hover { color: #1d4ed8; }
    ::ng-deep .ag-markdown-viewer ul,
    ::ng-deep .ag-markdown-viewer ol { margin: .75rem 0; padding-left: 1.75rem; }
    ::ng-deep .ag-markdown-viewer ul { list-style: disc outside; }
    ::ng-deep .ag-markdown-viewer ol { list-style: decimal outside; }
    ::ng-deep .ag-markdown-viewer li { margin: .25rem 0; }
    ::ng-deep .ag-markdown-viewer li > p { margin: 0; }
    ::ng-deep .ag-markdown-viewer ul:has(input[type='checkbox']) { padding-left: 0; list-style: none; }
    ::ng-deep .ag-markdown-viewer input[type='checkbox'] { margin: 0 .4rem 0 0; accent-color: #16a34a; vertical-align: middle; }
    ::ng-deep .ag-markdown-viewer input[type='checkbox']:checked { opacity: .8; }
    ::ng-deep .ag-markdown-viewer li:has(input[type='checkbox']:checked) { color: #64748b; text-decoration: line-through; }
    ::ng-deep .ag-markdown-viewer blockquote { margin: 1rem 0; padding-left: 1rem; border-left: 4px solid #94a3b8; color: #475569; }
    ::ng-deep .ag-markdown-viewer hr { margin: 1rem 0; border: 0; border-top: 1px solid #cbd5e1; }
    ::ng-deep .ag-markdown-viewer pre { margin: .75rem 0; overflow-x: auto; border-radius: .5rem; padding: .75rem; background: #0f172a; color: #e2e8f0; }
    ::ng-deep .ag-markdown-viewer code { border-radius: .25rem; padding: .1rem .25rem; background: #e2e8f0; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: .875em; }
    ::ng-deep .ag-markdown-viewer pre code { padding: 0; background: transparent; color: inherit; font-size: inherit; }
    ::ng-deep .ag-markdown-viewer table { display: block; width: 100%; overflow-x: auto; border-collapse: collapse; }
    ::ng-deep .ag-markdown-viewer th,
    ::ng-deep .ag-markdown-viewer td { border: 1px solid #94a3b8; min-width: 5rem; padding: .4rem; text-align: left; }
    ::ng-deep .ag-markdown-viewer th { background: #f8fafc; font-weight: 700; }
    ::ng-deep .ag-markdown-viewer img { display: block; max-width: 100%; height: auto; }
    ::ng-deep .ag-markdown-viewer del { color: #64748b; }
  `],
})
export class AgMarkdownCellRenderer implements ICellRendererAngularComp {
  params: ICellRendererParams | undefined;
  html: SafeHtml = '';

  private readonly sanitizer = inject(DomSanitizer);

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.setMarkdown(params.value);
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.setMarkdown(params.value);
    return true;
  }

  private setMarkdown(value: unknown): void {
    const markdown = value == null ? '' : String(value);
    const rendered = marked.parse(markdown, { gfm: true, breaks: true }) as string;
    const safeHtml = DOMPurify.sanitize(rendered, {
      USE_PROFILES: { html: true },
      ADD_TAGS: ['input'],
      ADD_ATTR: ['align', 'checked', 'class', 'disabled', 'start', 'type'],
    });
    this.html = this.sanitizer.bypassSecurityTrustHtml(safeHtml);
  }
}

/**
 * 股票報價單元格渲染器
 *
 * 用於顯示股票報價的單元格，根據值的正負自動變更顏色：
 * - 正值：紅色（上漲）
 * - 零值：白色（持平）
 * - 負值：綠色（下跌）
 */
@Component({
  selector: 'app-stockquote-renderer',
  standalone: true,
  imports: [FormsModule, NgStyle],
  template: `<div [ngStyle]="myStyle"  >{{params.value}} </div>`,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class AgStockQuoteCellView implements OnInit {
  params: any;
  constructor() {
  }
  agInit(params: any): void {
    this.params = params;
    this.setStyle();
  }
  ngOnInit() { }
  myStyle: any = undefined;
  setStyle() {
    let color = 'green';
    if (this.params.value === 0) {
      color = 'white';
    } else if (this.params.value > 0) {
      color = 'red';
    }
    this.myStyle = { 'background-color': 'black', color: color, 'text-align': 'right', 'width': this.params.colDef.width - 5 };
  }
}

/**
 * 文字區域編輯器
 *
 * 用於編輯多行文字的單元格編輯器。
 * 包含一個可展開的按鈕，點擊後會顯示全部文字內容。
 */
@Component({
  selector: 'app-textarea-renderer',
  standalone: true,
  imports: [FormsModule, MaterialModule],
  template: `
    <div class="flex">
      <textarea
      matImput
      class="w-full"
      [rows]="1"
      [(value)]="params.value"
      [matTooltip]="hintText"
      style="overflow: hidden;"
    >
    </textarea>
    <!-- <input
      matImput
      [value]="params.value"
      [matTooltip]="params.value"
    /> -->
    <button mat-mini-button class="jk-button-small" fill="outline" (click)="myAlert(params.value)" >...</button>

    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class AgTextAreaEditor implements OnInit {
  params: any;
  hintText: string = '';
  agInit(params: any): void {
    this.params = params;
    this.hintText = params.value; // .replace('\n','\r\n');
  }
  ngOnInit() { }
  myAlert(value: any) {
    //doTextArea(value, '內容');
    alert(value);
  }
}

/**
 * 固定大小文字區域編輯器
 *
 * 提供固定高度的多行文字輸入功能，
 * 相比標準文字區域編輯器，此版本沒有展開按鈕。
 */
@Component({
  selector: 'app-textarea-renderer-fix',
  standalone: true,
  imports: [FormsModule],
  template: `
    <textarea
      matImput
      class="jk-textarea-area"
      [rows]="3"
      [(value)]="params.value"
    >
    </textarea>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class AgTextAreaEditorFix implements OnInit {
  params: any;
  agInit(params: any): void {
    this.params = params;
  }
  ngOnInit() { }
}

/**
 * 勾選框編輯器
 *
 * 用於在AG Grid中顯示勾選框的單元格渲染器。
 * 將'Y'/'N'字串值轉換為布林值以供勾選框使用。
 * 注意：目前僅支援顯示，尚未實作編輯功能。
 */
@Component({
  selector: 'app-checkbox-renderer',
  standalone: true,
  imports: [FormsModule, MaterialModule],
  template: ` <mat-checkbox [checked]="value"> </mat-checkbox> `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class AgCheckBoxEditor implements OnInit {
  params: any;
  //value: boolean | undefined = undefined;
  get value(): boolean {
    return this.params.value === 'Y' ? true : false;
  }
  set value(value: boolean) {
    this.params.value = value ? 'Y' : 'N';
  }
  agInit(params: any): void {
    this.params = params;
    this.value = params.value === 'Y' ? true : false;
  }
  ngOnInit() { }
}

/**
 * 下拉選單編輯器
 *
 * 用於在AG Grid中顯示下拉選單的單元格編輯器。
 * 允許從預定義的選項列表中選擇值。
 */
@Component({
  selector: 'app-combobox-renderer',
  standalone: true,
  imports: [FormsModule, MaterialModule],
  template: `     <mat-select [(ngModel)]="params.value" class="w-full"
   >
    <mat-option value="">[空白]</mat-option> <!-- 空白選項 -->
    @for(idNameItem of items; track idNameItem) {
    <mat-option [value]="idNameItem.id">{{idNameItem.name}}</mat-option>
    }
  </mat-select>
`,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class AgComboBoxEditor implements OnInit {
  params: any;
  items: idNameItem[] = [];
  /*get value(): boolean {
    return this.params.value;
  }
  set value(value: boolean) {
    this.params.value = value;
  }*/
  agInit(params: any): void {
    this.params = params;
    this.items = params.list;
    //this.value = params.value;
  }
  ngOnInit() { }
}

/**
 * 全寬單元格渲染器
 *
 * 用於顯示跨越整個網格寬度的詳細資訊。
 * 通常用於顯示記錄的詳細內容，如主旨和完整描述。
 */
@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="example-full-width-row">
      <div class="full-width-summary">
        <label>
          <b>主旨</b>
          {{ data.WD004 }}
        </label>
        <br />
        <textarea>
                  {{ data.WD003 }}
              </textarea
        >
        <br />
      </div>
    </div>
  `,
})
export class FullWidthCellRenderer implements ICellRendererAngularComp {
  public data: any;

  agInit(params: ICellRendererParams): void {
    //this.data = params.node.data;
    this.data = params.node.data;
  }

  refresh(params: ICellRendererParams): boolean {
    return false;
  }
}

/**
 * 編輯按鈕單元格渲染器
 *
 * 用於在AG Grid中顯示編輯操作按鈕的單元格渲染器。
 * 對於群組節點不顯示編輯按鈕。
 */
@Component({
  selector: 'app-btnedit-renderer',
  standalone: true,
  imports: [FormsModule, MaterialModule],
  template: `
    <div class="actions-container">
      @if(!isGroupNode){
      <div class="w-6">
      <mat-icon fontIcon="edit_square" slot="start" name="edit_square-circle-outline"></mat-icon>

      </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      button {
        margin: 2px;
      }
      .actions-container {
        width: 100%;
      }
    `,
  ],
})
export class BtnEditCellRendererComponent {
  params: any;
  isGroupNode!: boolean;
  agInit(params: any): void {
    this.params = params;
    this.isGroupNode = params.node.group;
  }
  ngOnInit() { }
}

/**
 * 刪除按鈕單元格渲染器
 *
 * 用於在AG Grid中顯示刪除操作按鈕的單元格渲染器。
 * 對於群組節點不顯示刪除按鈕。
 */
@Component({
  selector: 'app-btndelete-renderer',
  standalone: true,
  imports: [FormsModule, MaterialModule],
  template: `
    <div class="actions-container">
      @if(!isGroupNode){
      <div class="w-6">
        <!-- <icon-trash-lines class="w-5 h-5 mr-2 shrink-0" /> -->
        <mat-icon fontIcon="delete" slot="start" name="delete-circle-outline"></mat-icon>
      </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      button {
        margin: 2px;
      }
      .actions-container {
        width: 100%;
      }
    `,
  ],
})
export class BtnDeleteCellRendererComponent implements OnInit {
  params: any;
  isGroupNode!: boolean;
  agInit(params: any): void {
    this.params = params;
    this.isGroupNode = params.node.group;
  }
  ngOnInit() { }
}

/**
 * 自訂按鈕組件
 *
 * 在單元格中顯示可點擊按鈕的渲染器。
 * 點擊時會顯示一個警告視窗。
 */
@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `<button class="btn btn-primary" (click)="buttonClicked()">
    Push Me!
  </button>`,
})
export class CustomButtonComponent implements ICellRendererAngularComp {
  private params!: ICellEditorParams;
  public value!: string;
  agInit(params: ICellRendererParams): void { }
  refresh(params: ICellRendererParams) {
    return true;
  }
  buttonClicked() {
    alert('clicked');
  }
}


/**
 * 數字編輯器
 *
 * 用於編輯數值的單元格編輯器。
 * 提供數字輸入欄位，可設定樣式如背景色、對齊方式等。
 */
@Component({
  standalone: true,
  imports: [FormsModule, NgStyle],
  selector: 'app-ag-number-editor',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `<input
    type="number"
    [(ngModel)]="value"
    [ngStyle]="myStyle"
    #input
  />`,
})
export class AgNumberEditor implements ICellEditorAngularComp, AfterViewInit {
  private params!: ICellEditorParams;
  public value!: number;

  public readonly input = viewChild.required('input', { read: ViewContainerRef });
  getValue() {
    // this simple editor doubles any value entered into the input
    return this.value;
  }

  ngAfterViewInit() {
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    this.value = this.params.value; //parseInt(this.params.value);
  }
  myStyle: any = undefined;
  setStyle() {
    let width = 100;
    if (this.params.colDef.width) {
      width = this.params.colDef.width - 5;
    }
    this.myStyle = { 'background-color': 'black', 'color': 'white', 'text-align': 'right', 'width': width };
  }
}
/**
 * 顯示百分比 0.01 => 1%
 */
@Component({
  standalone: true,
  imports: [FormsModule, NgStyle],
  selector: 'app-ag-percent-editor',
  template: `<input
    type="string"
    [(ngModel)]="value"
    [ngStyle]="myStyle"
    #input
  />`,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [``,
  ],
})
export class AgPercentEditor implements ICellEditorAngularComp, AfterViewInit {
  private params!: ICellEditorParams;
  public value!: string;

  public readonly input = viewChild.required('input', { read: ViewContainerRef });

  getValue() {
    return this.value;
  }

  ngAfterViewInit() { }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    const value = Math.round(this.params.value * 10000) / 100;
    this.value = value.toString() + '%';
  }

  myStyle: any = { 'text-align': 'right', 'width': '80px' };

}

/**
 * 數值倍增編輯器
 *
 * 編輯數值的單元格編輯器，將輸入的值乘以2作為最終值。
 * 特點：
 * 1. 自動聚焦於輸入欄位
 * 2. 當值大於1000時會拒絕編輯
 * 3. 支援zoneless模式下的變更檢測
 */
@Component({
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `<input
    type="number"
    [(ngModel)]="value"
    #input
    class="doubling-input"
  />`,
})
export class DoublingEditor implements ICellEditorAngularComp, AfterViewInit {
  private params!: ICellEditorParams;
  public value!: number;

  public readonly input = viewChild.required('input', { read: ViewContainerRef });

  ngAfterViewInit() {
    // focus on the input
    setTimeout(() => {
      this.input().element.nativeElement.focus();
    }, 1);
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;

    this.value = parseInt(this.params.value);
  }

  /* Component Editor Lifecycle methods */
  // the final value to send to the grid, on completion of editing
  getValue() {
    // this simple editor doubles any value entered into the input
    return this.value * 2;
  }

  // Gets called once before editing starts, to give editor a chance to
  // cancel the editing before it even starts.
  isCancelBeforeStart() {
    return false;
  }

  // Gets called once when editing is finished (eg if Enter is pressed).
  // If you return true, then the result of the edit will be ignored.
  isCancelAfterEnd() {
    // our editor will reject any value greater than 1000
    return this.value > 1000;
  }
}
